import { useEffect, useState } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { money, formatDate, formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { FileCheck2, Plus, Check, Download, CalendarCheck } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";

export default function Liquidaciones() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [genCompany, setGenCompany] = useState("");
  const [genOpen, setGenOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [confirmLiq, setConfirmLiq] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/liquidations");
      setList(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    (async () => {
      try {
        const { data } = await api.get("/companies");
        setCompanies(data.filter((c) => c.active));
      } catch {}
    })();
  }, []);

  const generate = async () => {
    if (!genCompany) return toast.error("Selecciona una empresa");
    try {
      await api.post("/liquidations/generate", null, { params: { company_id: genCompany } });
      toast.success("Liquidación generada");
      setGenOpen(false);
      setGenCompany("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const markReceived = async (id) => {
    setConfirmBusy(true);
    try {
      await api.post(`/liquidations/${id}/mark-received`);
      toast.success("Liquidación marcada como recibida");
      setConfirmLiq(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setConfirmBusy(false);
    }
  };

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/liquidations/${id}`);
      setDetail(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadPdf = async (id) => {
    try {
      const token = localStorage.getItem("nomiflex_token");
      const res = await fetch(`${API}/export/liquidations/${id}.pdf`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `liquidacion_${id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo descargar el PDF");
    }
  };

  return (
    <div className="space-y-5" data-testid="liquidaciones-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#0F172A]">Liquidaciones</h1>
          <p className="text-sm text-[#64748B] mt-1">Descuentos por empresa al día de nómina.</p>
        </div>
        {isAdmin && (
          <Dialog open={genOpen} onOpenChange={setGenOpen}>
            <DialogTrigger asChild>
              <Button data-testid="generate-liq-btn" className="bg-[#1D9E75] hover:bg-[#147D5C]">
                <Plus className="w-4 h-4 mr-1.5" />Generar liquidación
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Generar liquidación</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-[#64748B]">
                  Se incluirán todos los adelantos <strong>TRANSFERIDOS</strong> no liquidados de la empresa seleccionada.
                </p>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-[#475569]">Empresa</Label>
                  <Select value={genCompany} onValueChange={setGenCompany}>
                    <SelectTrigger data-testid="gen-company-select"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                    <SelectContent>
                      {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setGenOpen(false)}>Cancelar</Button>
                  <Button data-testid="gen-confirm-btn" onClick={generate} className="bg-[#1D9E75] hover:bg-[#147D5C]">Generar</Button>
                </DialogFooter>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="overflow-hidden p-0">
        {loading ? (
          <div className="p-8 flex justify-center"><div className="nomi-spin" /></div>
        ) : list.length === 0 ? (
          <div className="p-12 text-center">
            <FileCheck2 className="w-10 h-10 text-[#CBD5E1] mx-auto" />
            <div className="mt-3 text-sm text-[#64748B]">Aún no hay liquidaciones</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="liquidaciones-table">
              <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                <tr className="text-left text-[11px] uppercase tracking-wider text-[#64748B]">
                  <th className="px-4 py-3 font-medium">Generada</th>
                  <th className="px-4 py-3 font-medium">Empresa</th>
                  <th className="px-4 py-3 font-medium">Período</th>
                  <th className="px-4 py-3 font-medium text-right">Adelantos</th>
                  <th className="px-4 py-3 font-medium text-right">Total a devolver</th>
                  <th className="px-4 py-3 font-medium">Estado</th>
                  <th className="px-4 py-3 font-medium text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {list.map((l) => (
                  <tr key={l.id} className="border-b border-[#E2E8F0] hover:bg-[#F8FAFC]">
                    <td className="px-4 py-3 text-xs text-[#64748B]">{formatDateTime(l.generated_at)}</td>
                    <td className="px-4 py-3 font-medium text-[#0F172A]">{l.company_name}</td>
                    <td className="px-4 py-3">{l.period_label}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{l.advance_ids.length}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold text-[#147D5C]">{money(l.total_amount)}</td>
                    <td className="px-4 py-3"><StatusBadge status={l.status === "RECIBIDA" ? "RECIBIDA" : "PENDIENTE"} /></td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="outline" data-testid={`view-liq-${l.id}`} onClick={() => openDetail(l.id)} className="h-7">Ver</Button>
                        <Button size="sm" variant="outline" data-testid={`pdf-liq-${l.id}`} onClick={() => downloadPdf(l.id)} className="h-7">
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                        {isAdmin && l.status === "PENDIENTE" && (
                          <Button size="sm" data-testid={`mark-received-${l.id}`} onClick={() => setConfirmLiq(l)} className="bg-[#1D9E75] hover:bg-[#147D5C] h-7">
                            <Check className="w-3.5 h-3.5 mr-1" />Recibida
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Detalle de liquidación</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div><div className="text-xs uppercase text-[#64748B] tracking-wider">Empresa</div><div className="font-medium">{detail.company_name}</div></div>
                <div><div className="text-xs uppercase text-[#64748B] tracking-wider">Período</div><div className="font-medium">{detail.period_label}</div></div>
                <div><div className="text-xs uppercase text-[#64748B] tracking-wider">Estado</div><StatusBadge status={detail.status === "RECIBIDA" ? "RECIBIDA" : "PENDIENTE"} /></div>
              </div>
              <div className="overflow-x-auto border border-[#E2E8F0] rounded-md">
                <table className="w-full text-sm">
                  <thead className="bg-[#F8FAFC] text-[11px] uppercase tracking-wider text-[#64748B]">
                    <tr>
                      <th className="px-3 py-2 text-left">Empleado</th>
                      <th className="px-3 py-2 text-right">Monto</th>
                      <th className="px-3 py-2 text-right">Comisión</th>
                      <th className="px-3 py-2 text-right">IVA (16%)</th>
                      <th className="px-3 py-2 text-right">Total</th>
                      <th className="px-3 py-2 text-left">SPEI</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.advances.map((a) => (
                      <tr key={a.id} className="border-t border-[#E2E8F0]">
                        <td className="px-3 py-2">{a.employee_name}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{money(a.amount)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{money(a.fee)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{money(a.iva ?? (Number(a.fee || 0) * 0.16))}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold">{money(a.total_to_recover)}</td>
                        <td className="px-3 py-2 font-mono text-xs">{a.spei_reference || "—"}</td>
                      </tr>
                    ))}
                    <tr className="bg-[#F8FAFC] border-t-2 border-[#1D9E75]">
                      <td className="px-3 py-2 font-bold">TOTAL</td>
                      <td colSpan="3"></td>
                      <td className="px-3 py-2 text-right font-bold tabular-nums text-[#147D5C]">{money(detail.total_amount)}</td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Confirm mark-received dialog: shows the natural payday that will be
          stored as `último pago de nómina` for the included employees. */}
      <Dialog open={!!confirmLiq} onOpenChange={(v) => !v && setConfirmLiq(null)}>
        <DialogContent data-testid="confirm-received-dialog" className="sm:max-w-[460px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#0F172A]">
              <span className="inline-flex w-8 h-8 rounded-full bg-[#D1FAE5] text-[#147D5C] items-center justify-center">
                <CalendarCheck className="w-4 h-4" />
              </span>
              Marcar liquidación como recibida
            </DialogTitle>
            <DialogDescription className="text-sm text-[#64748B]">
              Esta acción liquidará todos los adelantos incluidos y reiniciará el ciclo de nómina de los empleados.
            </DialogDescription>
          </DialogHeader>

          {confirmLiq && (
            <div className="space-y-3 text-sm text-[#334155]">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <div className="uppercase tracking-wider text-[#64748B]">Empresa</div>
                  <div className="font-semibold text-[#0F172A] mt-0.5">{confirmLiq.company_name}</div>
                </div>
                <div>
                  <div className="uppercase tracking-wider text-[#64748B]">Adelantos</div>
                  <div className="font-semibold text-[#0F172A] mt-0.5">
                    {confirmLiq.advance_ids?.length || 0} · {money(confirmLiq.total_amount)}
                  </div>
                </div>
              </div>

              <div className="rounded-md border border-[#1D9E75]/30 bg-[#ECFDF5] p-3 space-y-1">
                <div className="text-[11px] uppercase tracking-wider text-[#147D5C] font-semibold flex items-center gap-1.5">
                  <CalendarCheck className="w-3.5 h-3.5" />
                  Último pago de nómina que se registrará
                </div>
                {confirmLiq.scheduled_per_employee ? (
                  <div className="text-sm text-[#0F172A]">
                    Se calculará <strong>por empleado</strong> (último pago + 7 días),
                    según el ciclo semanal de cada uno.
                  </div>
                ) : (
                  <div className="text-base font-bold text-[#147D5C]" data-testid="confirm-scheduled-date">
                    {formatDate(confirmLiq.scheduled_payout_date)}
                    <span className="ml-2 text-xs font-medium text-[#64748B] uppercase tracking-wider">
                      ({confirmLiq.company_payroll_period?.toLowerCase()})
                    </span>
                  </div>
                )}
                <div className="text-[11px] text-[#64748B] leading-snug">
                  La fecha se calcula sobre el día programado del periodo, no la fecha actual del clic.
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmLiq(null)}
              disabled={confirmBusy}
              data-testid="confirm-received-cancel"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={() => confirmLiq && markReceived(confirmLiq.id)}
              disabled={confirmBusy}
              className="bg-[#1D9E75] hover:bg-[#147D5C] text-white"
              data-testid="confirm-received-confirm"
            >
              {confirmBusy ? "Procesando..." : "Confirmar y marcar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
